import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
} from '@/components/ui';

/**
 * אישור לפני פעולה של המפעיל. עם `typeToConfirm` — הכפתור נפתח רק אחרי הקלדת
 * הערך המדויק (שם העסק במחיקה). השרת בודק את אותו שם שוב.
 */
export function ConfirmActionDialog({
  title,
  description,
  confirmLabel,
  danger,
  typeToConfirm,
  loading,
  onConfirm,
  onClose,
  children,
}: {
  title: string;
  description: string;
  confirmLabel: string;
  danger?: boolean;
  typeToConfirm?: string;
  loading?: boolean;
  onConfirm: (typed: string) => void;
  onClose: () => void;
  children?: ReactNode;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const [typed, setTyped] = useState('');
  const ready = typeToConfirm === undefined || typed.trim() === typeToConfirm.trim();

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent aria-describedby="confirm-action-desc">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription id="confirm-action-desc">{description}</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (ready && !loading) onConfirm(typed.trim());
          }}
        >
          {children || typeToConfirm !== undefined ? (
            <DialogBody>
              {children}
              {typeToConfirm !== undefined ? (
                <Field
                  label={t('actions.typeToConfirm', { name: typeToConfirm })}
                  htmlFor="confirm-action-typed"
                >
                  <Input
                    id="confirm-action-typed"
                    autoComplete="off"
                    autoFocus
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                  />
                </Field>
              ) : null}
            </DialogBody>
          ) : null}
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                {t('common:actions.cancel')}
              </Button>
            </DialogClose>
            <Button
              type="submit"
              variant={danger ? 'danger' : 'primary'}
              loading={loading}
              disabled={!ready}
            >
              {confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
